import { Box, IconButton, Grid, ListItemIcon, ListItemText, Menu, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@material-ui/core";
import { makeStyles, Theme } from '@material-ui/core/styles';
import { MoreVert, Send, UnfoldLess, UnfoldMore } from "@material-ui/icons";
import { Button, Card, ConfirmationMenuItem, ConfirmDialog, DataGrid, FileUploadModal, filterProps, Paper, PaperBorderType, PDFPreview } from "@roc/ui";
import { observer } from "mobx-react";
import { useState, useEffect } from "react";
import { PointOfContactTableRow } from "./pointOfContactTableRow";
import { DrawStore, PointOfContactStore, PointOfContactType } from '@roc/feature-draws';
import NewPointOfContactForm from "./NewPointOfContactForm";
import { useDocumentStore } from '@roc/feature-documents';
import { DownloadMobileAppDialog } from '../drawDetails/mobileSnapDrawAlert';
import { flatten } from "@roc/feature-utils";


const useStyles = makeStyles((theme: Theme) => ({
  noBorderCell: {
    border: 0,
  },
  borderCell: {
    border: '1px solid rgba(224, 224, 224, 1)'
  },
  addButton: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  cancelButton: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  table: {
    '& th': {
      borderBottom: '1px solid #eee'
    }
  },
  headerRow: {
    border: '1px solid #ccc',
    backgroundColor: '#f8f8f8',
    '& th': {
      padding: '8px'
    }
  }
}));

export type TierPricingEconomicsTableProps = {
  emptyMessage: string;
  store: PointOfContactStore;
  allowActions?: boolean;
  readOnly?: boolean
  drawStore: DrawStore
  isInternalValuationPropertyPhotosContactFlow?: boolean
};


export const PointsOfContactsTable = observer((
  { emptyMessage, store, isInternalValuationPropertyPhotosContactFlow, drawStore }: TierPricingEconomicsTableProps) => {
  const { pointOfContactsList, pointOfContactDrawMobileFormStore } = store;
  const { documentStore } = useDocumentStore();
  const [showDownload, setShowDownload] = useState(false);
  const classes = useStyles({});
  
  const setShowPointOfContactForm = (value: boolean) => {
    store?.setIsNewPointOfContactOpen(value)
  }

  const property = isInternalValuationPropertyPhotosContactFlow ? store.loanStore?.loanDetails?.properties[0]?.address : drawStore?.drawDTO?.draw?.propertyAddress;
  const drawDocuments = documentStore?.loanTodosStore?.todosByLoanProcess['draws'] ?? flatten(Object.values(documentStore.drawDocuments || []));
  const takePropertyPhotoDocument = drawDocuments?.find(doc => doc.document?.drawId === drawStore?.drawDTO?.draw?.drawId && doc.title === 'Take Property Photos');
  const takePropertyPhotoTaskId = isInternalValuationPropertyPhotosContactFlow ? documentStore?.currentDocument?.taskId : takePropertyPhotoDocument?.document?.taskId;

  const type = isInternalValuationPropertyPhotosContactFlow ? PointOfContactType.INTERNAL_VALUATION : PointOfContactType.DRAWS;
  const loanId = store.loanStore.loanDetails?.loanId;
  const drawId = drawStore?.drawDTO?.draw?.drawId;
  const propertyId = isInternalValuationPropertyPhotosContactFlow ? documentStore?.currentDocument?.collateralId : drawStore?.drawDTO?.draw?.propertyId;
  const filters = { type, loanId, drawId, propertyId, taskId:takePropertyPhotoTaskId  };
  

  useEffect(() => {
    if (takePropertyPhotoTaskId) {
      store.getPointOfContactsList(loanId, filters);
    }
  }, [loanId, takePropertyPhotoTaskId]);

  return (
    <Grid container>
      {isInternalValuationPropertyPhotosContactFlow &&
        <Grid container xs={12} style={{ marginBottom: 8 }}>
          <Box>
            With RealVal, you don’t need an appraisal inspection to get your loan approved. Instead, just download the mobile app and take photos of the property (or have your point of contact do it).{' '}
            <span
              style={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => setShowDownload(true)}
            >
              Download here
            </span><br />
            Once the property photos, valuation fee, and scope of work are provided, the internal valuation team can provide a quick and accurate value.
          </Box>
          <DownloadMobileAppDialog
            open={showDownload}
            onClose={() => setShowDownload(false)}
            lenderId={store.loanStore?.loanDetails?.lenderId}
          />
        </Grid>
      }
      {!store?.isNewPointFormOfContactOpen && <Grid container xs={12} justifyContent='flex-end'>
        <Button
          color="primary"
          variant="contained"
          className={classes.addButton}
          onClick={() => setShowPointOfContactForm(true)}
          testId="save">
          + Point of Contact
        </Button>
      </Grid>}
      {
        store?.isNewPointFormOfContactOpen && <Grid container xs={12} justifyContent='flex-end' spacing={2}>
          <NewPointOfContactForm />
          <Button
            color="secondary"
            variant="outlined"
            className={classes.cancelButton}
            onClick={() => setShowPointOfContactForm(false)}
            testId="cancel">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            className={classes.addButton}
            onClick={() => store.savePointOfContactMobileSnap(store.loanStore?.loanDetails?.loanId, filters,
              {
                ...pointOfContactDrawMobileFormStore.getFormValues(),
                propertyAddress: property,
                taskIds: [takePropertyPhotoTaskId],
                type: type,
                propertyId: propertyId,
                drawId: drawId
              })}
            testId="save">
            Save
          </Button>
        </Grid>
      }
      {!store?.isNewPointFormOfContactOpen && <Table>
        <TableHead>
          <TableRow className={classes.headerRow}>
            <TableCell>Contact Name</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Cellphone</TableCell>
            <TableCell>Created Date</TableCell>
            <TableCell>Invite Code</TableCell>
            <TableCell style={{textAlign:'center'}}>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pointOfContactsList?.length == 0 ?
            <TableRow>
              <TableCell colSpan={5} className={classes.borderCell}>
                <Box textAlign={'center'}>
                  {emptyMessage}
                </Box>
              </TableCell>
            </TableRow>
            :
            pointOfContactsList?.map((contact) => (
              <PointOfContactTableRow
                store={store}
                contact={contact} />
            ))
          }
        </TableBody>
      </Table>}
    </Grid>
  )
});

